import Phaser from 'phaser';
import { Entity, EntityConfig } from './Entity';
import { EntityManager } from './EntityManager';
import { Monster } from './Monster';
import { Boss } from './Boss';
import { COLORS, GAME } from '../constants';
import { VFXSystem } from '../systems/VFXSystem';
import { LevelStatsTracker } from '../systems/LevelStatsTracker';
import { AudioRouter } from '../systems/AudioRouter';
import { InputRouter } from '../systems/InputRouter';
import { Player } from './Player';

// Union type for anything a tower can target
type Targetable = Monster | Boss;

// ============================================
// Tower Entity - Full Implementation
// Based on towers_monsters_paths_design.md
// ============================================

export type TowerType =
  // Tier 1 - Starter (free to research)
  | 'arrow'      // All targets, balanced DPS
  | 'cannon'     // Ground Only, splash damage
  | 'antiair'    // Air Only, fast anti-air
  // Tier 2 - Research Gated (available but costs gems to unlock)
  | 'tesla'      // Ground Only, burst strike + chain
  | 'laser'      // Air Only, continuous beam, pierce
  | 'mortar'     // Ground Only, long range artillery
  // Tier 3 - Special Unlock (unlock via special stages, then research)
  | 'ice'        // Ground, slow field
  | 'fire'       // Ground, DoT burn
  | 'hive'       // All targets, swarm attackers
  | 'lightning'  // All targets, global strike (limit 1)
  | 'trap'       // Link trap between towers
  | 'gem';       // Support buff tower

export type TargetType = 'ground' | 'air' | 'both' | 'none';

// Upgrade stat categories
export type UpgradeStat = 'range' | 'power' | 'speed' | 'spread' | 'strength';

export interface TowerStats {
  damage: number;
  range: number;
  fireRate: number;        // Attacks per second
  cost: number;            // Coin cost to build
  researchCost: number;    // Gem cost to research/unlock (0 = starter)
  color: number;
  projectileSpeed: number;
  targetType: TargetType;
  upgradePath: UpgradeStat[];  // 4 upgrades: Green→Yellow→Red→Purple→Black
  splashRadius?: number;   // For AoE towers
  slowPercent?: number;    // For ice tower
  slowDuration?: number;   // For ice tower (ms)
  burnDamage?: number;     // For fire tower (DPS)
  burnDuration?: number;   // For fire tower (ms)
  chainCount?: number;     // For tesla tower
  pierceCount?: number;    // For laser tower
  buffRadius?: number;     // For gem tower
  buffPercent?: number;    // For gem tower
  limit?: number;          // Max instances (e.g., lightning = 1)
  tier: number;
}

const TOWER_STATS: Record<TowerType, TowerStats> = {
  // ============================================
  // Tier 1 - Starter Towers (free to research)
  // ============================================
  arrow: {
    damage: 12,
    range: 130,
    fireRate: 1.2,
    cost: 100,           // 100 coins
    researchCost: 0,     // Free (starter)
    color: 0x8b4513,     // Brown
    projectileSpeed: 400,
    targetType: 'both',
    upgradePath: ['range', 'range', 'range', 'power'],
    tier: 1,
  },
  cannon: {
    damage: 35,
    range: 110,
    fireRate: 0.5,
    cost: 120,           // 120 coins
    researchCost: 0,     // Free (starter)
    color: 0x696969,     // Gray
    projectileSpeed: 250,
    targetType: 'ground',
    splashRadius: 98,    // +30% (was 75) — splash damage radius only, not tower range
    upgradePath: ['spread', 'power', 'power', 'range'],
    tier: 1,
  },
  antiair: {
    damage: 9,   // -25% from 12
    range: 120,
    fireRate: 3.0,
    cost: 150,           // 150 coins
    researchCost: 0,     // Free (starter)
    color: 0x4169e1,     // Royal Blue
    projectileSpeed: 500,
    targetType: 'air',
    upgradePath: ['speed', 'speed', 'speed', 'power'],
    tier: 1,
  },

  // ============================================
  // Tier 2 - Research-Gated Towers (costs gems)
  // ============================================
  tesla: {
    damage: 45,
    range: 90,
    fireRate: 0.6,
    cost: 300,           // 300 coins
    researchCost: 10,    // 10 gems to research
    color: 0xffff00,     // Yellow
    projectileSpeed: 1000,
    targetType: 'ground',
    chainCount: 3,
    upgradePath: ['power', 'power', 'power', 'power'],
    tier: 2,
  },
  laser: {
    damage: 8,           // Per tick
    range: 180,
    fireRate: 10,        // Continuous ticks
    cost: 300,           // 300 coins
    researchCost: 11,    // 11 gems to research
    color: 0xff00ff,     // Magenta
    projectileSpeed: 0,  // Instant beam
    targetType: 'air',
    pierceCount: 3,
    upgradePath: ['speed', 'speed', 'range', 'power'],
    tier: 2,
  },
  mortar: {
    damage: 120,         // 2x (was 60)
    range: 320,          // +60% (was 200)
    fireRate: 0.1,       // 3x longer reload (was 0.3)
    cost: 400,           // 400 coins
    researchCost: 15,    // 15 gems to research
    color: 0x8b0000,     // Dark Red
    projectileSpeed: 150,
    targetType: 'ground',
    splashRadius: 140,   // 2x (was 70)
    upgradePath: ['spread', 'spread', 'spread', 'speed'],
    tier: 2,
  },

  // ============================================
  // Tier 3 - Special Unlock Towers
  // ============================================
  ice: {
    damage: 5,
    range: 100,
    fireRate: 1.5,
    cost: 200,           // 200 coins
    researchCost: 9,     // 9 gems to research
    color: 0x00bfff,     // Deep Sky Blue
    projectileSpeed: 300,
    targetType: 'ground',
    slowPercent: 0.5,
    slowDuration: 2000,
    upgradePath: ['range', 'speed', 'spread', 'power'],
    tier: 3,
  },
  fire: {
    damage: 3,           // Initial hit
    range: 120,          // +50% (was 80)
    fireRate: 2.0,
    cost: 350,           // 350 coins
    researchCost: 13,    // 13 gems to research
    color: 0xff4500,     // Orange Red
    projectileSpeed: 0,
    targetType: 'ground',
    burnDamage: 8,
    burnDuration: 3000,
    upgradePath: ['spread', 'range', 'power', 'power'],
    tier: 3,
  },
  hive: {
    damage: 10,
    range: 120,
    fireRate: 0.8,
    cost: 450,           // 450 coins
    researchCost: 14,    // 14 gems to research
    color: 0xffa500,     // Orange
    projectileSpeed: 200,
    targetType: 'both',
    upgradePath: ['range', 'speed', 'power', 'power'],
    tier: 3,
  },
  lightning: {
    damage: 80,          // High damage, global strike
    range: 999,          // Hits anywhere
    fireRate: 0.25,      // Slow
    cost: 500,           // 500 coins
    researchCost: 13,    // 13 gems to research
    color: 0x00ffff,     // Cyan
    projectileSpeed: 9999, // Instant
    targetType: 'both',
    limit: 1,            // Only one allowed
    upgradePath: ['strength', 'strength', 'strength', 'strength'],
    tier: 3,
  },
  trap: {
    damage: 20,          // Per second while in beam
    range: 150,
    fireRate: 0,
    cost: 250,           // 250 coins
    researchCost: 10,    // 10 gems to research
    color: 0x00ff00,     // Green
    projectileSpeed: 0,
    targetType: 'both',
    upgradePath: ['power', 'power', 'range', 'power'],
    tier: 3,
  },
  gem: {
    damage: 0,
    range: 100,
    fireRate: 0,
    cost: 300,           // 300 coins
    researchCost: 9,     // 9 gems to research
    color: 0x9400d3,     // Dark Violet
    projectileSpeed: 0,
    targetType: 'none',
    buffRadius: 100,
    buffPercent: 0.25,
    upgradePath: ['range', 'range', 'range', 'range'],
    tier: 3,
  },
};

export interface TowerConfig extends Omit<EntityConfig, 'type'> {
  towerType: TowerType;
  level?: number;
  pairedTowerId?: string;  // For trap towers
}

export class Tower extends Entity {
  public readonly towerType: TowerType;
  private stats: TowerStats;
  private level: number;
  private fireCooldown: number = 0;
  private target: Targetable | null = null;
  private pairedTowerId: string | null = null;

  // Visuals
  private body: Phaser.GameObjects.Rectangle | Phaser.GameObjects.Image;
  private typeLabel: Phaser.GameObjects.Text;
  private levelLabel: Phaser.GameObjects.Text;
  private useImage: boolean = false;
  private rangeCircle: Phaser.GameObjects.Arc | null = null;
  
  // Upgrade flag system
  private flagPole: Phaser.GameObjects.Rectangle | null = null;
  private flagPennant: Phaser.GameObjects.Triangle | null = null;
  private flagWaveOffset: number = 0;

  // For laser tower - continuous beam
  private laserBeam: Phaser.GameObjects.Graphics | null = null;
  private laserTargets: Targetable[] = [];

  // For fire tower - cone effect
  private fireCone: Phaser.GameObjects.Graphics | null = null;

  // For gem tower - buff aura
  private buffAura: Phaser.GameObjects.Arc | null = null;

  // For trap tower - beam between paired towers
  private trapBeam: Phaser.GameObjects.Graphics | null = null;

  // For tesla tower - frame-loop animation (50ms per frame, no rotation)
  private teslaFrameTimer: number = 0;
  private teslaFrameIndex: number = 0;
  private static readonly TESLA_FRAME_MS = 50;
  private static readonly TESLA_FRAME_COUNT = 6;

  // Stats tracking
  private damageDealt: number = 0;
  private killCount: number = 0;

  // Unified upgrade progression (used by both kills and player dancing)
  private upgradeProgress: number = 0;  // 0 to 1
  private static readonly KILLS_FOR_UPGRADE_BASE = 15;  // Base kills needed for level 1->2
  private static readonly KILLS_MULTIPLIER_PER_LEVEL = 1.5;  // Each level needs 1.5x more kills

  // Buff tracking
  private damageMultiplier: number = 1;

  // Construction system
  private isUnderConstruction: boolean = true;
  private constructionTime: number = 0;      // Total construction time in seconds
  private constructionProgress: number = 0;  // Current progress in seconds
  private constructionImage: Phaser.GameObjects.Image | null = null;
  private constructionProgressBar: Phaser.GameObjects.Rectangle | null = null;
  private constructionProgressBarBg: Phaser.GameObjects.Rectangle | null = null;
  private constructionCountdown: Phaser.GameObjects.Text | null = null;
  private static readonly CONSTRUCTION_IMAGES = ['construction-1', 'construction-2', 'construction-3', 'construction-4'];
  private static readonly SECONDS_PER_100_COINS = 5; // 100 coins = 5 seconds

  constructor(scene: Phaser.Scene, config: TowerConfig) {
    super(scene, { ...config, type: 'tower' });

    this.towerType = config.towerType;
    this.level = config.level ?? 1;
    this.pairedTowerId = config.pairedTowerId ?? null;
    this.stats = this.calculateStats();

    // Try to use tower image, fall back to colored rectangle
    const imageKey = `tower-${this.towerType}`;
    if (scene.textures.exists(imageKey)) {
      this.useImage = true;
      const img = scene.add.image(0, 0, imageKey);
      img.setScale(0.28); // Smaller tower images
      this.body = img;
    } else {
      // Fallback to colored rectangle placeholder
      const rect = scene.add.rectangle(0, 0, GAME.TOWER_SIZE, GAME.TOWER_SIZE, this.stats.color);
      rect.setStrokeStyle(2, 0xffffff);
      this.body = rect;
    }

    // Tower type icon (hidden if using image)
    const iconText = this.getTowerIcon();
    this.typeLabel = scene.add.text(0, -5, iconText, {
      fontSize: '14px',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    this.typeLabel.setOrigin(0.5);
    if (this.useImage) {
      this.typeLabel.setVisible(false);
    }

    this.levelLabel = scene.add.text(0, 28, `Lv${this.level}`, {
      fontSize: '10px',
      color: '#ffffff',
      stroke: '#000000',
      strokeThickness: 2,
    });
    this.levelLabel.setOrigin(0.5);

    this.container.add([this.body, this.typeLabel, this.levelLabel]);

    // Create upgrade flag
    this.createUpgradeFlag();

    // Create special visuals for certain tower types
    this.createSpecialVisuals();

    // Y-based depth for 2.5D sorting (bottom of visual - lower on screen = in front)
    this.updateDepth();

    // Random wave offset for flags so they don't all sync
    this.flagWaveOffset = Math.random() * Math.PI * 2;

    // Initialize construction state
    this.initializeConstruction();
  }

  /**
   * Bottom of tower visual for depth sorting.
   * Body is at (0,0) in container with center origin, so bottom = _y + displayHeight/2.
   * Use explicit calculation instead of GetBottom (which returns wrong values for towers).
   * +2 tiebreaker so towers render in front of units when bottoms align.
   */
  protected getDepthY(): number {
    const bodyHeight = (this.body as Phaser.GameObjects.Components.ComputedSize).displayHeight ?? GAME.TOWER_SIZE;
    return this._y + bodyHeight / 2 + 2;
  }

  private initializeConstruction(): void {
    // Calculate construction time based on cost (5 seconds per 100 coins)
    const cost = this.stats.cost;
    this.constructionTime = (cost / 100) * Tower.SECONDS_PER_100_COINS;
    this.constructionProgress = 0;
    this.isUnderConstruction = true;

    // Hide actual tower visuals during construction
    this.body.setVisible(false);
    this.typeLabel.setVisible(false);
    this.levelLabel.setVisible(false);
    if (this.flagPole) this.flagPole.setVisible(false);
    if (this.flagPennant) this.flagPennant.setVisible(false);

    // Create construction image (start with first stage)
    const constructionKey = Tower.CONSTRUCTION_IMAGES[0];
    if (this.scene.textures.exists(constructionKey)) {
      this.constructionImage = this.scene.add.image(0, 0, constructionKey);
      this.constructionImage.setScale(0.28);
      this.container.add(this.constructionImage);
    }

    // Create construction progress bar background
    this.constructionProgressBarBg = this.scene.add.rectangle(0, -45, 50, 8, 0x333333);
    this.constructionProgressBarBg.setStrokeStyle(1, 0x000000);
    this.container.add(this.constructionProgressBarBg);

    // Create construction progress bar fill
    this.constructionProgressBar = this.scene.add.rectangle(-25, -45, 0, 6, 0x00ff00);
    this.constructionProgressBar.setOrigin(0, 0.5);
    this.container.add(this.constructionProgressBar);

    // Create countdown text
    this.constructionCountdown = this.scene.add.text(0, -58, `${Math.ceil(this.constructionTime)}s`, {
      fontSize: '12px',
      color: '#ffffff',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
    });
    this.constructionCountdown.setOrigin(0.5);
    this.container.add(this.constructionCountdown);

    // Start looping construction sound
    AudioRouter.startConstruction(this.id);
  }

  private getTowerIcon(): string {
    const icons: Record<TowerType, string> = {
      arrow: '→',
      cannon: '●',
      antiair: '↑',
      tesla: '⚡',
      laser: '═',
      mortar: '◎',
      ice: '❄',
      fire: '🔥',
      hive: '⬡',
      gem: '◆',
      trap: '⊗',
      lightning: '⚡',
    };
    return icons[this.towerType] || '?';
  }

  /**
   * Flash the tower body with a color (works for both Rectangle and Image)
   */
  private flashBody(color: number, duration: number = 100): void {
    if (this.useImage) {
      (this.body as Phaser.GameObjects.Image).setTint(color);
      this.scene.time.delayedCall(duration, () => {
        if (!this.destroyed) {
          (this.body as Phaser.GameObjects.Image).clearTint();
        }
      });
    } else {
      (this.body as Phaser.GameObjects.Rectangle).setStrokeStyle(3, color);
      this.scene.time.delayedCall(duration, () => {
        if (!this.destroyed) {
          (this.body as Phaser.GameObjects.Rectangle).setStrokeStyle(2, 0xffffff);
        }
      });
    }
  }

  /**
   * Set the body border/stroke style (only for Rectangle, no-op for Image)
   */
  private setBodyStroke(width: number, color: number): void {
    if (!this.useImage) {
      (this.body as Phaser.GameObjects.Rectangle).setStrokeStyle(width, color);
    }
  }

  // ============================================
  // Upgrade Flag System
  // ============================================

  // Flag colors by level: Green → Yellow → Red → Purple → Black → Rainbow (dance-only)
  private static readonly FLAG_COLORS: Array<{ fill: number; border: number; name: string }> = [
    { fill: 0x2ECC71, border: 0x1E8F4D, name: 'green' },   // Level 1
    { fill: 0xF1C40F, border: 0xB7950B, name: 'yellow' }, // Level 2
    { fill: 0xE74C3C, border: 0xA93226, name: 'red' },     // Level 3
    { fill: 0x8E44AD, border: 0x5B2C6F, name: 'purple' }, // Level 4
    { fill: 0x111111, border: 0x000000, name: 'black' },  // Level 5
    { fill: 0xE40303, border: 0xFF8C00, name: 'rainbow' }, // Level 6 (dance-only, +25% damage)
  ];

  private createUpgradeFlag(): void {
    // Flag pole - extends from inside tower body up above it
    const poleX = -18;
    const poleY = 20; // Bottom of pole extends into tower body  
    const poleHeight = 100; // Longer pole to extend well above tower

    this.flagPole = this.scene.add.rectangle(poleX, poleY, 4, poleHeight, 0x4a3728);
    this.flagPole.setOrigin(0.5, 1); // Anchor at bottom
    this.container.add(this.flagPole);

    // Flag pennant - triangular pennant (2x size)
    const flagColor = this.getFlagColor();
    const flagX = poleX + 2;
    const flagY = poleY - poleHeight + 8; // Raised flag position

    // Triangle points doubled: (0,0) top-left, (24, 10) right point, (0, 20) bottom-left
    this.flagPennant = this.scene.add.triangle(
      flagX, flagY,
      0, 0,     // Top left
      24, 10,   // Right point
      0, 20,    // Bottom left
      flagColor.fill
    );
    this.flagPennant.setOrigin(0, 0.5);
    this.flagPennant.setStrokeStyle(2, flagColor.border);
    this.container.add(this.flagPennant);

    // Set flag depth BEHIND tower body (negative depth so tower renders on top)
    this.flagPole.setDepth(-1);
    this.flagPennant.setDepth(-1);
    
    // Send flag to back of container so tower image renders in front
    this.container.sendToBack(this.flagPennant);
    this.container.sendToBack(this.flagPole);
  }

  private getFlagColor(): { fill: number; border: number; name: string } {
    const levelIndex = Math.min(this.level - 1, Tower.FLAG_COLORS.length - 1);
    return Tower.FLAG_COLORS[levelIndex];
  }

  private updateFlagColor(animate: boolean = false): void {
    if (!this.flagPennant) return;

    const flagColor = this.getFlagColor();

    if (animate) {
      // Level-up animation: burst + color change
      this.playFlagLevelUpEffect(flagColor);
    } else {
      this.flagPennant.setFillStyle(flagColor.fill);
      this.flagPennant.setStrokeStyle(1, flagColor.border);
    }
  }

  private playFlagLevelUpEffect(newColor: { fill: number; border: number; name: string }): void {
    if (!this.flagPennant || !this.flagPole) return;

    // Spawn burst particles matching new flag color
    for (let i = 0; i < 12; i++) {
      const particle = this.scene.add.circle(
        this.container.x + this.flagPole.x,
        this.container.y + this.flagPole.y - 15,
        3, newColor.fill
      );
      particle.setDepth(100);

      const angle = (i / 12) * Math.PI * 2;
      const dist = 20 + Math.random() * 15;

      this.scene.tweens.add({
        targets: particle,
        x: particle.x + Math.cos(angle) * dist,
        y: particle.y + Math.sin(angle) * dist,
        alpha: 0,
        scale: 0.5,
        duration: 350,
        ease: 'Power2',
        onComplete: () => particle.destroy()
      });
    }

    // Animate flag drop-in from above
    const originalY = this.flagPennant.y;
    this.flagPennant.y = originalY - 20;
    this.flagPennant.setFillStyle(newColor.fill);
    this.flagPennant.setStrokeStyle(1, newColor.border);

    this.scene.tweens.add({
      targets: this.flagPennant,
      y: originalY,
      duration: 300,
      ease: 'Bounce.easeOut'
    });

    // Scale bounce
    this.scene.tweens.add({
      targets: this.flagPennant,
      scaleX: 1.3,
      scaleY: 1.3,
      duration: 100,
      yoyo: true,
      ease: 'Quad.easeOut'
    });
  }

  private updateFlagWave(): void {
    if (!this.flagPennant) return;

    // Sine wave animation for flag flutter
    const time = this.scene.time.now / 1000;
    const waveSpeed = 1.6; // Period in seconds
    const baseAmplitude = 6; // Base rotation in degrees
    
    // Higher levels have stronger wave motion
    const levelMultiplier = 0.8 + (this.level * 0.1);
    const amplitude = baseAmplitude * levelMultiplier;

    const rotation = Math.sin((time * (2 * Math.PI / waveSpeed)) + this.flagWaveOffset) * Phaser.Math.DegToRad(amplitude);
    this.flagPennant.setRotation(rotation);

    // Black (5) and Rainbow (6) get pulse effect and glow
    if (this.level >= 5) {
      const pulseScale = 1 + Math.sin(time * 2) * 0.08;
      this.flagPennant.setScale(pulseScale);
      
      // Add colored stroke based on tower type for max level
      const glowColor = this.getMaxLevelGlowColor();
      const glowIntensity = 0.7 + Math.sin(time * 3) * 0.3;
      this.flagPennant.setStrokeStyle(2, glowColor, glowIntensity);
    }
  }

  private getMaxLevelGlowColor(): number {
    // Glow colors based on tower type for max level
    switch (this.towerType) {
      case 'arrow':
      case 'cannon':
      case 'mortar':
        return 0xffa500; // Orange
      case 'tesla':
      case 'laser':
      case 'lightning':
        return 0x00bfff; // Blue
      case 'fire':
        return 0xff4500; // Red
      case 'ice':
        return 0x00ffff; // Cyan
      case 'gem':
        return 0xffd700; // Gold
      default:
        return 0xffffff; // White
    }
  }

  private createSpecialVisuals(): void {
    // Laser beam graphics
    if (this.towerType === 'laser') {
      this.laserBeam = this.scene.add.graphics();
      this.laserBeam.setDepth(55);
    }

    // Fire cone graphics
    if (this.towerType === 'fire') {
      this.fireCone = this.scene.add.graphics();
      this.fireCone.setDepth(55);
    }

    // Gem buff aura
    if (this.towerType === 'gem') {
      this.buffAura = this.scene.add.circle(0, 0, this.stats.buffRadius!, 0x9400d3, 0.2);
      this.buffAura.setStrokeStyle(2, 0x9400d3, 0.5);
      this.container.add(this.buffAura);
      this.container.sendToBack(this.buffAura);
    }

    // Trap beam graphics
    if (this.towerType === 'trap') {
      this.trapBeam = this.scene.add.graphics();
      this.trapBeam.setDepth(55);
    }
  }

  private calculateStats(): TowerStats {
    const base = TOWER_STATS[this.towerType];
    // Level scaling: each level adds 20% damage, 5% range (level 1–5)
    const effectiveLevel = Math.min(this.level, 5);
    const levelMultiplier = 1 + (effectiveLevel - 1) * 0.2;
    const rangeMultiplier = 1 + (effectiveLevel - 1) * 0.05;
    let damage = Math.round(base.damage * levelMultiplier);
    // Rainbow (level 6): +25% damage, dance-only
    if (this.level >= 6) {
      damage = Math.round(damage * 1.25);
    }
    let fireRate = base.fireRate;
    // Anti-air purple (level 4+): 48 DPS (proportional to red's 24), so fireRate = 48/damage
    if (this.towerType === 'antiair' && effectiveLevel >= 4) {
      fireRate = 48 / damage;
    }
    return {
      ...base,
      damage,
      range: Math.round(base.range * rangeMultiplier),
      fireRate,
    };
  }

  update(delta: number): void {
    // Handle construction progress
    if (this.isUnderConstruction) {
      this.updateConstruction(delta);
      return; // Don't do anything else while under construction
    }

    // Apply buffs from nearby gem towers
    this.updateBuffs();

    // Animate upgrade flag wave
    this.updateFlagWave();

    // Reduce cooldown
    this.fireCooldown -= delta / 1000;

    // Handle different tower types
    switch (this.towerType) {
      case 'laser':
        this.updateLaser(delta);
        break;
      case 'fire':
        this.updateFire(delta);
        break;
      case 'gem':
        this.updateGem(delta);
        break;
      case 'trap':
        this.updateTrap(delta);
        break;
      case 'lightning':
        this.updateLightning(delta);
        break;
      default:
        this.updateStandard(delta);
        break;
    }
  }

  private updateConstruction(delta: number): void {
    this.constructionProgress += delta / 1000;

    // Calculate progress percentage
    const progressPercent = Math.min(this.constructionProgress / this.constructionTime, 1);

    // Update construction image based on progress (4 stages)
    const imageIndex = Math.min(
      Math.floor(progressPercent * Tower.CONSTRUCTION_IMAGES.length),
      Tower.CONSTRUCTION_IMAGES.length - 1
    );
    const constructionKey = Tower.CONSTRUCTION_IMAGES[imageIndex];
    
    if (this.constructionImage && this.scene.textures.exists(constructionKey)) {
      this.constructionImage.setTexture(constructionKey);
    }

    // Update progress bar
    if (this.constructionProgressBar) {
      this.constructionProgressBar.setSize(50 * progressPercent, 6);
    }

    // Update countdown text
    const remainingTime = Math.max(0, this.constructionTime - this.constructionProgress);
    if (this.constructionCountdown) {
      this.constructionCountdown.setText(`${Math.ceil(remainingTime)}s`);
    }

    // Check if construction is complete
    if (this.constructionProgress >= this.constructionTime) {
      this.completeConstruction();
    }
  }

  private completeConstruction(): void {
    this.isUnderConstruction = false;

    // Stop construction sound
    AudioRouter.stopConstruction(this.id);

    // Remove construction visuals
    if (this.constructionImage) {
      this.constructionImage.destroy();
      this.constructionImage = null;
    }
    if (this.constructionProgressBar) {
      this.constructionProgressBar.destroy();
      this.constructionProgressBar = null;
    }
    if (this.constructionProgressBarBg) {
      this.constructionProgressBarBg.destroy();
      this.constructionProgressBarBg = null;
    }
    if (this.constructionCountdown) {
      this.constructionCountdown.destroy();
      this.constructionCountdown = null;
    }

    // Show actual tower visuals
    this.body.setVisible(true);
    this.typeLabel.setVisible(this.useImage ? false : true); // Only show icon if no image
    this.levelLabel.setVisible(true);
    if (this.flagPole) this.flagPole.setVisible(true);
    if (this.flagPennant) this.flagPennant.setVisible(true);

    // Recalculate depth now that tower body is visible (GetBottom was unreliable during construction)
    this.updateDepth();

    // Play completion effect
    this.playConstructionCompleteEffect();
  }

  private playConstructionCompleteEffect(): void {
    // Sparkle effect on completion
    for (let i = 0; i < 8; i++) {
      const angle = (i / 8) * Math.PI * 2;
      const particle = this.scene.add.circle(
        this.container.x + Math.cos(angle) * 20,
        this.container.y + Math.sin(angle) * 20,
        4, 0xffff00
      );
      particle.setDepth(100);

      this.scene.tweens.add({
        targets: particle,
        x: particle.x + Math.cos(angle) * 30,
        y: particle.y + Math.sin(angle) * 30,
        alpha: 0,
        scale: 0,
        duration: 400,
        ease: 'Quad.easeOut',
        onComplete: () => particle.destroy()
      });
    }

    // Scale pop animation on tower
    this.scene.tweens.add({
      targets: this.body,
      scaleX: 0.33,
      scaleY: 0.33,
      duration: 100,
      yoyo: true,
      ease: 'Quad.easeOut',
      onComplete: () => {
        if (this.useImage) {
          (this.body as Phaser.GameObjects.Image).setScale(0.28);
        }
      }
    });
  }

  // ============================================
  // Standard Attack Logic
  // ============================================

  private updateStandard(_delta: number): void {
    // Find target
    if (!this.target || this.target.destroyed || !this.isValidTarget(this.target)) {
      this.target = this.findBestTarget();
    }

    // Arrow tower: update aim direction to face target
    if (this.towerType === 'arrow' && this.useImage && this.body instanceof Phaser.GameObjects.Image) {
      this.updateArrowAim();
    }

    // Cannon tower: update aim direction to face target (same 1–8 convention)
    if (this.towerType === 'cannon' && this.useImage && this.body instanceof Phaser.GameObjects.Image) {
      this.updateTowerAimDirection('cannon');
    }

    // Anti-air tower: update aim direction to face target (same 1–8 convention)
    if (this.towerType === 'antiair' && this.useImage && this.body instanceof Phaser.GameObjects.Image) {
      this.updateTowerAimDirection('antiair');
    }

    // Mortar tower: update aim direction to face target (same 1–8 convention)
    if (this.towerType === 'mortar' && this.useImage && this.body instanceof Phaser.GameObjects.Image) {
      this.updateTowerAimDirection('mortar');
    }

    // Tesla tower: frame-loop animation (50ms per frame, no rotation)
    if (this.towerType === 'tesla' && this.useImage && this.body instanceof Phaser.GameObjects.Image) {
      this.updateTeslaAnimation(_delta);
    }

    // Attack if ready
    if (this.target && this.fireCooldown <= 0) {
      this.attackStandard(this.target);
      this.fireCooldown = 1 / this.stats.fireRate;
    }
  }

  /**
   * Set arrow tower body texture to aim at current target.
   * Frame convention: 1=right, 2=down-right, 3=down, 4=down-left, 5=left, 6=up-left, 7=up, 8=up-right.
   * When there is no target, the tower keeps its last aim direction.
   */
  private updateArrowAim(): void {
    this.updateTowerAimDirection('arrow');
  }

  /**
   * Tesla tower: frame-loop animation. Cycles through frames 01–06 every 50ms.
   * Does NOT use rotation/aim logic; just a static looping animation.
   */
  private updateTeslaAnimation(delta: number): void {
    this.teslaFrameTimer += delta;
    if (this.teslaFrameTimer >= Tower.TESLA_FRAME_MS) {
      this.teslaFrameTimer -= Tower.TESLA_FRAME_MS;
      this.teslaFrameIndex = (this.teslaFrameIndex + 1) % Tower.TESLA_FRAME_COUNT;
      const frame = this.teslaFrameIndex + 1;
      const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
      const textureKey = `tower-tesla-${pad(frame)}`;
      if (this.scene.textures.exists(textureKey)) {
        (this.body as Phaser.GameObjects.Image).setTexture(textureKey);
      }
    }
  }

  /**
   * Set tower body texture to aim at current target (1–8 direction).
   * Aims at shooting target if in range, otherwise at nearest valid target (so tower tracks enemies even when it can't fire).
   */
  private updateTowerAimDirection(towerType: 'arrow' | 'cannon' | 'antiair' | 'laser' | 'mortar' | 'fire'): void {
    const prefix = `tower-${towerType}`;
    // Use shooting target if valid, else aim at nearest enemy (any range) so tower tracks even when it can't hit
    const aimTarget = (this.target && !this.target.destroyed)
      ? this.target
      : this.findNearestTargetForAim();
    if (!aimTarget) {
      return; // Keep current texture (last aim direction)
    }
    const dx = aimTarget.x - this._x;
    const dy = aimTarget.y - this._y;
    const angle = Math.atan2(dy, dx);
    const normalized = (angle + 2 * Math.PI) % (2 * Math.PI);
    const frame = 1 + Math.floor((normalized / (2 * Math.PI)) * 8) % 8;
    const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
    const textureKey = `${prefix}-${pad(frame)}`;
    if (this.scene.textures.exists(textureKey)) {
      (this.body as Phaser.GameObjects.Image).setTexture(textureKey);
    } else if (towerType === 'arrow') {
      // Arrow fallback to nearest loaded frame (01, 03, 05, 07) if in-betweens not loaded
      const fallback = frame <= 2 ? '01' : frame <= 4 ? '03' : frame <= 6 ? '05' : '07';
      const fallbackKey = `${prefix}-${fallback}`;
      if (this.scene.textures.exists(fallbackKey)) {
        (this.body as Phaser.GameObjects.Image).setTexture(fallbackKey);
      }
    }
  }

  /**
   * Nearest target in range for aim direction. Ignores target type (ground/air) so towers aim at
   * any enemy in range even if they can't shoot it (e.g. anti-air aims at ground, ground tower aims at air).
   */
  private findNearestTargetForAim(): Targetable | null {
    const targets = this.getAllTargets()
      .filter(t => !t.destroyed && this.distanceTo(t) <= this.stats.range)
      .sort((a, b) => this.distanceTo(a) - this.distanceTo(b));
    return targets.length > 0 ? targets[0] : null;
  }

  private attackStandard(target: Targetable): void {
    const damage = Math.round(this.stats.damage * this.damageMultiplier);

    // Play tower fire sound effect (antiair at black: play back-to-back)
    AudioRouter.playTowerFire(this.towerType, {
      playTwice: this.towerType === 'antiair' && this.level >= 5,
    });

    // Arrow, cannon, mortar: defer damage until projectile reaches target
    const isDeferredDamage = this.towerType === 'arrow' || this.towerType === 'cannon' || this.towerType === 'mortar';
    const impactX = target.x;
    const impactY = target.y;

    if (!isDeferredDamage) {
      // Apply damage immediately for non-projectile towers
      this.applyAttackDamage(target, damage, impactX, impactY);
    }

    // Visual feedback (with onHit callback for deferred damage towers)
    const onHit = isDeferredDamage
      ? () => this.applyAttackDamage(target, damage, impactX, impactY)
      : undefined;
    this.showAttackVisual(target, onHit);
  }

  /** Apply damage, splash, chain, etc. Called immediately or from projectile onHit callback. */
  private applyAttackDamage(target: Targetable, damage: number, impactX: number, impactY: number): void {
    if (target.destroyed) return;

    const result = target.takeDamage(damage, this.towerType);
    this.damageDealt += result.damageDealt;
    LevelStatsTracker.recordDamage(this.towerType, result.damageDealt, result.killed);
    if (result.killed) {
      this.recordKill();
    }

    if (target instanceof Monster) {
      if (this.towerType === 'ice' && this.stats.slowPercent && this.stats.slowDuration) {
        target.applySlow(this.stats.slowPercent, this.stats.slowDuration);
      }
    }

    if (this.towerType === 'cannon' && this.stats.splashRadius) {
      this.applySplashDamage(impactX, impactY, this.stats.splashRadius, damage * 0.5, target);
    }

    if (this.towerType === 'mortar' && this.stats.splashRadius) {
      this.applySplashDamage(impactX, impactY, this.stats.splashRadius, damage * 0.7, target);
    }

    if (this.towerType === 'tesla' && this.stats.chainCount) {
      this.applyChainDamage(target, damage * 0.6, this.stats.chainCount);
    }
  }

  // ============================================
  // Laser Tower Logic
  // ============================================

  private updateLaser(_delta: number): void {
    if (!this.laserBeam) return;

    // Update aim direction (1–8) to face nearest air target
    if (this.useImage && this.body instanceof Phaser.GameObjects.Image) {
      this.updateTowerAimDirection('laser');
    }

    // Find all air targets in range
    this.laserTargets = this.findTargetsInRange('air', this.stats.pierceCount || 1);

    // Clear previous beam
    this.laserBeam.clear();

    if (this.laserTargets.length > 0) {
      // Draw beam through all targets
      this.laserBeam.lineStyle(3, 0xff00ff, 0.8);

      let startX = this._x;
      let startY = this._y;

      for (const target of this.laserTargets) {
        this.laserBeam.lineBetween(startX, startY, target.x, target.y);
        startX = target.x;
        startY = target.y;

        // Apply continuous damage
        const damage = Math.round(this.stats.damage * this.damageMultiplier);
        const result = target.takeDamage(damage, this.towerType);
        this.damageDealt += result.damageDealt;
        LevelStatsTracker.recordDamage(this.towerType, result.damageDealt, result.killed);
        if (result.killed) {
          this.recordKill();
        }
      }
    }
  }

  // ============================================
  // Fire Tower Logic
  // ============================================

  private updateFire(_delta: number): void {
    if (!this.fireCone) return;

    // Update aim direction to face target (8-frame rotation)
    if (this.useImage && this.body instanceof Phaser.GameObjects.Image) {
      this.updateTowerAimDirection('fire');
    }

    // Get targets in cone
    const targets = this.findTargetsInCone(60); // 60 degree cone

    // Clear previous cone
    this.fireCone.clear();

    // Only draw fire cone and deal damage when there are enemies in range
    if (targets.length > 0) {
      // Draw fire cone
      this.drawFireCone();

      for (const target of targets) {
        // Apply burn DoT (only for monsters)
        if (this.stats.burnDamage && this.stats.burnDuration && target instanceof Monster) {
          target.applyBurn(this.stats.burnDamage, this.stats.burnDuration);
        }

        // Apply initial damage
        if (this.fireCooldown <= 0) {
          const damage = Math.round(this.stats.damage * this.damageMultiplier);
          const result = target.takeDamage(damage, this.towerType);
          this.damageDealt += result.damageDealt;
          LevelStatsTracker.recordDamage(this.towerType, result.damageDealt, result.killed);
          if (result.killed) {
            this.recordKill();
          }
        }
      }

      if (this.fireCooldown <= 0) {
        this.fireCooldown = 1 / this.stats.fireRate;
      }
    }
  }

  /** Offset from tower center to nozzle (fire origin for visuals only) */
  private static readonly FIRE_NOZZLE_OFFSET = 24;

  private drawFireCone(): void {
    if (!this.fireCone) return;

    const range = this.stats.range;
    const coneAngle = Math.PI / 3; // 60 degrees

    // Find direction to closest target or use default
    let targetAngle = 0;
    const targets = this.findTargetsInRange('ground', 1);
    if (targets.length > 0) {
      targetAngle = Phaser.Math.Angle.Between(this._x, this._y, targets[0].x, targets[0].y);
    }

    // Origin at nozzle (in front of tower center) - visual only, damage logic unchanged
    const nozzleX = this._x + Math.cos(targetAngle) * Tower.FIRE_NOZZLE_OFFSET;
    const nozzleY = this._y + Math.sin(targetAngle) * Tower.FIRE_NOZZLE_OFFSET;

    // Layered radial falloff - brighter near source, fades toward tip (like real flame spray)
    const a1 = targetAngle - coneAngle / 2;
    const a2 = targetAngle + coneAngle / 2;
    const layers = [
      { r: range * 0.3, alpha: 0.14, color: 0xff6600 },   // Inner hot core
      { r: range * 0.55, alpha: 0.09, color: 0xff4500 },  // Mid
      { r: range * 0.8, alpha: 0.05, color: 0xff3300 },   // Outer
      { r: range, alpha: 0.02, color: 0xcc2200 },         // Faint tip
    ];
    let prevR = 0;
    for (const layer of layers) {
      this.fireCone.fillStyle(layer.color, layer.alpha);
      this.fireCone.beginPath();
      if (prevR === 0) {
        this.fireCone.moveTo(nozzleX, nozzleY);
        this.fireCone.lineTo(nozzleX + Math.cos(a1) * layer.r, nozzleY + Math.sin(a1) * layer.r);
        this.fireCone.arc(nozzleX, nozzleY, layer.r, a1, a2);
      } else {
        this.fireCone.moveTo(nozzleX + Math.cos(a1) * prevR, nozzleY + Math.sin(a1) * prevR);
        this.fireCone.lineTo(nozzleX + Math.cos(a1) * layer.r, nozzleY + Math.sin(a1) * layer.r);
        this.fireCone.arc(nozzleX, nozzleY, layer.r, a1, a2);
        this.fireCone.lineTo(nozzleX + Math.cos(a2) * prevR, nozzleY + Math.sin(a2) * prevR);
        this.fireCone.arc(nozzleX, nozzleY, prevR, a2, a1, true);
      }
      this.fireCone.closePath();
      this.fireCone.fillPath();
      prevR = layer.r;
    }

    // Add particle VFX (from nozzle)
    VFXSystem.spawnFireCone(nozzleX, nozzleY, targetAngle, range);
  }

  private findTargetsInCone(coneAngleDegrees: number): Targetable[] {
    const targets = this.getAllTargets();
    const coneAngle = Phaser.Math.DegToRad(coneAngleDegrees);
    const results: Targetable[] = [];

    // Get direction to closest target
    const closest = this.findBestTarget();
    if (!closest) return results;

    const targetAngle = Phaser.Math.Angle.Between(this._x, this._y, closest.x, closest.y);

    for (const target of targets) {
      if (!this.canTarget(target)) continue;

      const dist = this.distanceTo(target);
      if (dist > this.stats.range) continue;

      const targetPosAngle = Phaser.Math.Angle.Between(this._x, this._y, target.x, target.y);
      let angleDiff = Math.abs(targetPosAngle - targetAngle);
      if (angleDiff > Math.PI) angleDiff = 2 * Math.PI - angleDiff;

      if (angleDiff <= coneAngle / 2) {
        results.push(target);
      }
    }

    return results;
  }

  // ============================================
  // Gem Tower Logic
  // ============================================

  private updateGem(_delta: number): void {
    // Gem tower doesn't attack, just provides buff aura
    // Buff is applied via updateBuffs() on other towers
  }

  // ============================================
  // Lightning Tower Logic (hits all monsters)
  // ============================================

  private updateLightning(_delta: number): void {
    if (this.fireCooldown > 0) return;

    // Get ALL monsters and bosses on screen
    const allTargets = this.getAllTargets();
    if (allTargets.length === 0) return;

    // Play lightning/tesla sound effect
    AudioRouter.playTowerFire('tesla');

    const damage = Math.round(this.stats.damage * this.damageMultiplier);

    // Collect target positions for VFX
    const targetPositions: Array<{ x: number; y: number }> = [];

    // Deal damage to all enemies
    for (const target of allTargets) {
      const result = target.takeDamage(damage, this.towerType);
      this.damageDealt += result.damageDealt;
      LevelStatsTracker.recordDamage(this.towerType, result.damageDealt, result.killed);
      if (result.killed) {
        this.recordKill();
      }
      targetPositions.push({ x: target.x, y: target.y });
    }

    // Spawn lightning VFX
    VFXSystem.spawnLightningStrike(this._x, this._y, targetPositions);

    // Flash tower
    this.flashBody(0x00ffff, 100);

    this.fireCooldown = 1 / this.stats.fireRate;
  }

  private updateBuffs(): void {
    // Reset multiplier
    this.damageMultiplier = 1;

    if (this.towerType === 'gem') return; // Gem towers don't buff themselves

    // Find nearby gem towers
    const towers = EntityManager.getByType<Tower>('tower');
    for (const tower of towers) {
      if (tower.towerType !== 'gem') continue;
      if (tower.destroyed) continue;

      const dist = this.distanceTo(tower);
      if (dist <= (tower.stats.buffRadius || 0)) {
        this.damageMultiplier += tower.stats.buffPercent || 0;
      }
    }
  }

  // ============================================
  // Trap Tower Logic
  // ============================================

  private updateTrap(_delta: number): void {
    if (!this.trapBeam || !this.pairedTowerId) return;

    // Find paired tower
    const pairedTower = EntityManager.get<Tower>(this.pairedTowerId);
    if (!pairedTower || pairedTower.destroyed) {
      this.trapBeam.clear();
      return;
    }

    // Draw beam between towers
    this.trapBeam.clear();
    this.trapBeam.lineStyle(4, 0x00ff00, 0.6);
    this.trapBeam.lineBetween(this._x, this._y, pairedTower.x, pairedTower.y);

    // Damage enemies crossing the beam and track XP
    const targets = this.getAllTargets();
    for (const target of targets) {
      if (this.isOnBeam(target, pairedTower)) {
        const damage = Math.round(this.stats.damage * this.damageMultiplier * (_delta / 1000));
        const result = target.takeDamage(damage, this.towerType);
        this.damageDealt += result.damageDealt;
        LevelStatsTracker.recordDamage(this.towerType, result.damageDealt, result.killed);
        if (result.killed) {
          this.recordKill();
        }
      }
    }
  }

  private isOnBeam(target: Targetable, pairedTower: Tower): boolean {
    // Check if target is close to the line between this and paired tower
    const dist = Phaser.Math.Distance.BetweenPointsSquared(
      { x: this._x, y: this._y },
      { x: pairedTower.x, y: pairedTower.y }
    );

    const t = Math.max(0, Math.min(1, (
      (target.x - this._x) * (pairedTower.x - this._x) +
      (target.y - this._y) * (pairedTower.y - this._y)
    ) / dist));

    const closestX = this._x + t * (pairedTower.x - this._x);
    const closestY = this._y + t * (pairedTower.y - this._y);

    const distToLine = Phaser.Math.Distance.Between(target.x, target.y, closestX, closestY);
    return distToLine < 20; // 20 pixel threshold
  }

  // ============================================
  // Targeting Helpers
  // ============================================

  /**
   * Get all targetable entities (monsters + bosses)
   */
  private getAllTargets(): Targetable[] {
    const monsters = EntityManager.getByType<Monster>('monster');
    const bosses = EntityManager.getByType<Boss>('boss');
    return [...monsters, ...bosses];
  }

  private findBestTarget(): Targetable | null {
    const targets = this.getAllTargets();

    // Filter to valid targets in range
    const inRange = targets.filter(target => this.isValidTarget(target));

    if (inRange.length === 0) return null;

    // Sort by path progress (nearest to base first)
    inRange.sort((a, b) => b.getPathProgress() - a.getPathProgress());

    return inRange[0];
  }

  private isValidTarget(target: Targetable): boolean {
    if (target.destroyed) return false;
    if (!this.canTarget(target)) return false;
    return this.distanceTo(target) <= this.stats.range;
  }

  private canTarget(target: Targetable): boolean {
    const movementType = target.getMovementType();

    switch (this.stats.targetType) {
      case 'ground':
        return movementType === 'ground';
      case 'air':
        return movementType === 'air';
      case 'both':
        return true;
      case 'none':
        return false;
    }
  }

  private findTargetsInRange(targetType: 'ground' | 'air' | 'both', maxCount: number): Targetable[] {
    const targets = this.getAllTargets();
    const results: Targetable[] = [];

    // Sort by distance
    const sorted = targets
      .filter(t => {
        if (t.destroyed) return false;
        const mt = t.getMovementType();
        if (targetType === 'ground' && mt !== 'ground') return false;
        if (targetType === 'air' && mt !== 'air') return false;
        return this.distanceTo(t) <= this.stats.range;
      })
      .sort((a, b) => this.distanceTo(a) - this.distanceTo(b));

    for (let i = 0; i < Math.min(maxCount, sorted.length); i++) {
      results.push(sorted[i]);
    }

    return results;
  }

  // ============================================
  // Damage Helpers
  // ============================================

  private applySplashDamage(x: number, y: number, radius: number, damage: number, primaryTarget?: Targetable): void {
    const targets = this.getAllTargets();
    const skipTarget = primaryTarget ?? this.target;

    for (const target of targets) {
      if (target === skipTarget) continue;
      if (!this.canTarget(target)) continue;

      const dist = Phaser.Math.Distance.Between(x, y, target.x, target.y);
      if (dist <= radius) {
        const falloff = 1 - (dist / radius) * 0.5;
        const actualDamage = Math.round(damage * falloff);
        const result = target.takeDamage(actualDamage, this.towerType);
        this.damageDealt += result.damageDealt;
        LevelStatsTracker.recordDamage(this.towerType, result.damageDealt, result.killed);
        if (result.killed) {
          this.recordKill();
        }
      }
    }

    // Vibrate gamepads for players within blast radius
    const players = EntityManager.getByType<Player>('player');
    for (const player of players) {
      if (player.destroyed) continue;
      const dist = Phaser.Math.Distance.Between(x, y, player.x, player.y);
      if (dist <= radius) {
        InputRouter.vibratePlayerGamepad(player.getPlayerId(), 120);
      }
    }

    // Show explosion
    this.showExplosion(x, y, radius);
  }

  private applyChainDamage(source: Targetable, damage: number, chainCount: number): void {
    const hit = new Set<string>([source.id]);
    let current: Targetable = source;

    for (let i = 0; i < chainCount; i++) {
      // Find closest unhit target
      const targets = this.getAllTargets();
      let closest: Targetable | null = null;
      let closestDist = 100; // Max chain range

      for (const target of targets) {
        if (hit.has(target.id)) continue;
        if (!this.canTarget(target)) continue;

        const dist = Phaser.Math.Distance.Between(current.x, current.y, target.x, target.y);
        if (dist < closestDist) {
          closestDist = dist;
          closest = target;
        }
      }

      if (!closest) break;

      // Show chain lightning
      this.showChainLightning(current, closest);

      // Apply damage
      const result = closest.takeDamage(Math.round(damage), this.towerType);
      this.damageDealt += result.damageDealt;
      LevelStatsTracker.recordDamage(this.towerType, result.damageDealt, result.killed);
      if (result.killed) {
        this.recordKill();
      }

      hit.add(closest.id);
      current = closest;
      damage *= 0.8; // Reduce damage each chain
    }
  }

  // ============================================
  // Visual Effects
  // ============================================

  private showAttackVisual(target: Targetable, onHit?: () => void): void {
    // Use VFX based on tower type (no body flash — animation only)
    switch (this.towerType) {
      case 'arrow': {
        // Spawn arrow from upper part of tower graphic (bow/crossbow)
        const arrowOriginOffsetY = -29;
        VFXSystem.spawnArrowProjectile({
          startX: this._x,
          startY: this._y + arrowOriginOffsetY,
          endX: target.x,
          endY: target.y,
          color: this.stats.color,
          duration: 120,
          onHit,
        });
        }
        break;

      case 'cannon':
        VFXSystem.spawnCannonProjectile({
          startX: this._x,
          startY: this._y,
          endX: target.x,
          endY: target.y,
          color: this.stats.color,
          duration: 350,
          size: 8,
          onHit,
        });
        break;

      case 'mortar':
        VFXSystem.spawnMortarProjectile({
          startX: this._x,
          startY: this._y,
          endX: target.x,
          endY: target.y,
          color: this.stats.color,
          duration: 500,
          size: 12,
          onHit,
        });
        break;

      case 'antiair':
        VFXSystem.spawnAntiAirBullet({
          startX: this._x,
          startY: this._y,
          endX: target.x,
          endY: target.y,
          color: this.stats.color
        });
        break;

      case 'tesla':
        VFXSystem.spawnTeslaArc(this._x, this._y, target.x, target.y);
        break;

      case 'ice':
        VFXSystem.spawnIcePulse(this._x, this._y, this.stats.range);
        VFXSystem.spawnIceParticles(target.x, target.y);
        break;

      case 'lightning':
        // Lightning tower uses special global attack (handled elsewhere)
        VFXSystem.spawnHitSpark(target.x, target.y, 0x00ffff);
        break;

      case 'fire':
        // Fire has its own continuous effect
        break;

      default:
        // Default projectile line for other towers
        VFXSystem.spawnHitSpark(target.x, target.y, this.stats.color);
        break;
    }
  }

  private showExplosion(x: number, y: number, radius: number): void {
    // Use VFX based on tower type
    if (this.towerType === 'mortar') {
      VFXSystem.spawnMortarExplosion({ x, y, color: this.stats.color, radius });
    } else {
      VFXSystem.spawnCannonExplosion({ x, y, color: this.stats.color, radius });
    }
  }

  private showChainLightning(from: Targetable | { x: number; y: number }, to: Targetable): void {
    VFXSystem.spawnTeslaArc(from.x, from.y, to.x, to.y);
  }

  /**
   * Show/hide range indicator
   */
  showRange(show: boolean): void {
    if (show && !this.rangeCircle) {
      this.rangeCircle = this.scene.add.circle(0, 0, this.stats.range);
      this.rangeCircle.setStrokeStyle(2, 0xffffff, 0.5);
      this.rangeCircle.setFillStyle(0xffffff, 0.1);
      this.container.add(this.rangeCircle);
      this.container.sendToBack(this.rangeCircle);
    } else if (!show && this.rangeCircle) {
      this.rangeCircle.destroy();
      this.rangeCircle = null;
    }
  }

  /**
   * Upgrade the tower (level 6 = rainbow, dance-only)
   */
  upgrade(): boolean {
    if (this.level >= 6) return false;

    this.level++;
    this.stats = this.calculateStats();
    this.levelLabel.setText(`Lv${this.level}`);

    // Update flag with level-up animation
    this.updateFlagColor(true);

    // Visual feedback on tower body
    this.flashBody(COLORS.ACCENT, 300);

    return true;
  }

  // ============================================
  // Cleanup
  // ============================================

  destroy(): void {
    // Stop construction sound if still building
    if (this.isUnderConstruction) {
      AudioRouter.stopConstruction(this.id);
    }
    
    this.laserBeam?.destroy();
    this.fireCone?.destroy();
    this.trapBeam?.destroy();
    this.flagPole?.destroy();
    this.flagPennant?.destroy();
    // Clean up construction visuals
    this.constructionImage?.destroy();
    this.constructionProgressBar?.destroy();
    this.constructionProgressBarBg?.destroy();
    this.constructionCountdown?.destroy();
    super.destroy();
  }

  // ============================================
  // Getters
  // ============================================

  getDamage(): number { return this.stats.damage; }
  getRange(): number { return this.stats.range; }
  getFireRate(): number { return this.stats.fireRate; }
  getCost(): number { return this.stats.cost; }
  getLevel(): number { return this.level; }
  getTier(): number { return this.stats.tier; }
  getTargetType(): TargetType { return this.stats.targetType; }
  getDamageDealt(): number { return this.damageDealt; }
  getKillCount(): number { return this.killCount; }
  isBuilding(): boolean { return this.isUnderConstruction; }
  getConstructionProgress(): number { 
    return this.constructionTime > 0 ? this.constructionProgress / this.constructionTime : 1; 
  }
  
  // Unified upgrade progress (0 to 1) - used by both kills and player dancing
  getUpgradeProgress(): number { return this.upgradeProgress; }
  setUpgradeProgress(progress: number): void { 
    this.upgradeProgress = Math.max(0, Math.min(1, progress)); 
  }
  addUpgradeProgress(amount: number): boolean {
    if (this.level >= 6) return false; // Max level (rainbow)
    
    this.upgradeProgress += amount;
    
    // Check for level up
    if (this.upgradeProgress >= 1) {
      this.upgradeProgress = 0;
      this.upgrade();
      return true; // Leveled up
    }
    return false;
  }
  getCurrentTarget(): Targetable | null { return this.target; }
  getSplashRadius(): number { return this.stats.splashRadius || 0; }
  getSlowPercent(): number { return this.stats.slowPercent || 0; }
  getSlowDuration(): number { return this.stats.slowDuration || 0; }

  // ============================================
  // Kill-Based Upgrade System
  // ============================================

  /**
   * Get the number of kills needed for the next level upgrade
   */
  private getKillsForNextLevel(): number {
    const multiplier = Math.pow(Tower.KILLS_MULTIPLIER_PER_LEVEL, this.level - 1);
    return Math.round(Tower.KILLS_FOR_UPGRADE_BASE * multiplier);
  }

  /**
   * Record a kill and progress toward next level
   * Returns true if the tower leveled up
   */
  recordKill(): boolean {
    if (this.level >= 6) return false; // Max level (rainbow)

    this.killCount++;
    this.damageDealt += 1; // Also track damage dealt

    // Add progress toward next level (uses unified progress system)
    const killsNeeded = this.getKillsForNextLevel();
    const progressPerKill = 1 / killsNeeded;
    const leveledUp = this.addUpgradeProgress(progressPerKill);
    
    if (leveledUp) {
      console.log(`[Tower] ${this.towerType} leveled up from kills! Now Lv${this.level}`);
    }

    return leveledUp;
  }

  /**
   * Add damage dealt (for stats tracking)
   */
  addDamageDealt(damage: number): void {
    this.damageDealt += damage;
  }

  // ============================================
  // Leveling Multipliers
  // ============================================

  /**
   * Apply range multiplier (from leveling)
   */
  applyRangeMultiplier(multiplier: number): void {
    this.stats.range = Math.round(this.stats.range * multiplier);
    // Update buff radius for gem tower
    if (this.stats.buffRadius) {
      this.stats.buffRadius = Math.round(this.stats.buffRadius * multiplier);
      if (this.buffAura) {
        this.buffAura.setRadius(this.stats.buffRadius);
      }
    }
  }

  /**
   * Apply cooldown multiplier (from leveling - lower = faster)
   */
  applyCooldownMultiplier(multiplier: number): void {
    // fireRate is attacks per second, so we need inverse
    this.stats.fireRate = this.stats.fireRate / multiplier;
  }

  /**
   * Apply damage multiplier (from leveling)
   */
  applyDamageMultiplier(multiplier: number): void {
    this.stats.damage = Math.round(this.stats.damage * multiplier);
    // Also scale burn damage for fire tower
    if (this.stats.burnDamage) {
      this.stats.burnDamage = Math.round(this.stats.burnDamage * multiplier);
    }
  }

  /**
   * Apply spread multiplier (AoE radius or slow/burn duration)
   */
  applySpreadMultiplier(multiplier: number): void {
    // Splash radius for cannon/mortar
    if (this.stats.splashRadius) {
      this.stats.splashRadius = Math.round(this.stats.splashRadius * multiplier);
    }
    // Slow duration for ice tower
    if (this.stats.slowDuration) {
      this.stats.slowDuration = Math.round(this.stats.slowDuration * multiplier);
    }
    // Slow strength for ice tower
    if (this.stats.slowPercent) {
      this.stats.slowPercent = Math.min(0.9, this.stats.slowPercent * multiplier);
    }
    // Burn duration for fire tower
    if (this.stats.burnDuration) {
      this.stats.burnDuration = Math.round(this.stats.burnDuration * multiplier);
    }
  }

  /**
   * Set the level color indicator (flag color)
   * Now uses the existing flag pennant system instead of creating a separate indicator
   */
  setLevelColor(color: number): void {
    // Update existing flag pennant color if it exists
    if (this.flagPennant) {
      this.flagPennant.setFillStyle(color);
      this.flagPennant.setStrokeStyle(2, Phaser.Display.Color.ValueToColor(color).darken(30).color);
    }

    // Update border color to match level (only for rectangle placeholders)
    this.setBodyStroke(2, color);
  }

  static getCost(towerType: TowerType): number {
    return TOWER_STATS[towerType].cost;
  }

  static getStats(towerType: TowerType): TowerStats {
    return { ...TOWER_STATS[towerType] };
  }

  static getTier(towerType: TowerType): number {
    return TOWER_STATS[towerType].tier;
  }

  static getAllTypes(): TowerType[] {
    return Object.keys(TOWER_STATS) as TowerType[];
  }

  static getTypesByTier(tier: number): TowerType[] {
    return (Object.entries(TOWER_STATS) as [TowerType, TowerStats][])
      .filter(([_, stats]) => stats.tier === tier)
      .map(([type, _]) => type);
  }
}
